import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Collapse, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { useAppSelector } from '@/redux/store.hook';
import { getAuthSelector } from '@/redux/auth/auth.slice';

import { Wrapper, CollapsWrapper, UnreadCounter } from './styles';
import { getPblNotifications } from '@/api/pbl.api';
import { getNotificationSelector } from '@/redux/notification/notification.slice';
import useResize from '@/hooks/useResize';
import LanguageSelector from '@/components/UI/LanguageSelector';
import { getCookie } from '@/utils/cookie';
import useCurrentLang from '@/hooks/useCurrentLang';
import PATH from '@/router/path';
import { resolveAiMediaNavigationLinks } from '@/utils/aiMedia';
import { isQualificationOnboarding } from '@/components/DmpV2/qualificationGuidance';
import { resolveBlockchainNavigationLinks } from '@/utils/blockchainDocuments';
import { getPblLawyerVettingStatus, hasPblRole, isPblCitizen, isPblLawyer, isPblOcAdminOrStaff } from '@/utils/pblRole';
import { isPblApplicationContext, isPblApplicationPath } from '@/utils/applications';
import { getPblAssistanceToken } from '@/utils/pblAssistance';

interface SidebarProps {
  setBurgerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const Sidebar = ({ setBurgerOpen }: SidebarProps) => {
  // @ts-ignore
  const { t } = useTranslation();

  const { size, isMobile, isTablet } = useResize();
  const location = useLocation();
  const guidanceEnglish = useCurrentLang() === 'en';
  const { pathname } = location;
  const auth = useAppSelector(getAuthSelector);
  const notifications = useAppSelector(getNotificationSelector);
  const { isLogin, accessToken } = auth;
  const { notify } = notifications;
  const [topSpace, setTopSpace] = useState(133);
  const cookiePolicy: string | null = getCookie('cookiePolicy');

  const sidebarLinks = resolveBlockchainNavigationLinks(resolveAiMediaNavigationLinks(t('pages.sidebarLinks', { returnObjects: true }) as any[]));
  const isRegistrationLikePath =
    pathname === '/registration' ||
    pathname === '/termsUpdate' ||
    /^\/(en|tc|sc)\/volunteerRegistration\/?$/.test(pathname);

  // 建立滾軸偵測, 超過150px就將topSpace設為60px
  window.addEventListener('scroll', () => {
    const isAccept = cookiePolicy ? 0 : 95;

    if (isTablet) {
      setTopSpace(133 + isAccept);
      return;
    }
    if (window.scrollY > 133) {
      setTopSpace(32);
    } else {
      setTopSpace(133 + isAccept);
    }
  });

  // 在notify變化時重新刷新未讀通知數量
  useEffect(() => {}, [notify]);

  useEffect(() => {
    const isAccept = cookiePolicy ? 0 : 95;
    if (isTablet) {
      setTopSpace(133 + isAccept);
    } else {
      setTopSpace(133 + isAccept);
    }
  }, [size, isTablet]);

  return (
    <Wrapper style={{ paddingTop: topSpace }} isMobile={isMobile}>
      {isLogin && isQualificationOnboarding(pathname, location.search) ? (
        <List aria-label={guidanceEnglish ? 'Account setup navigation' : '帳號設定導覽'}>
          {[
            {
              path: PATH.profile.identity + location.search,
              name: '身分資料與資格',
              en: 'Identity and qualifications',
            },
            { path: '/profile/subscriptionDetail', name: '付款與訂閱紀錄', en: 'Payments and subscriptions' },
          ].map((item) => (
            <ListItem button component="a" href={item.path} key={item.path} onClick={() => setBurgerOpen(false)}>
              <ListItemText primary={guidanceEnglish ? item.en : item.name} />
            </ListItem>
          ))}
        </List>
      ) : isRegistrationLikePath ? (
        <List className="disabled">
          {sidebarLinks.map((pages) => (
            <ListItem button key={pages.name}>
              <ListItemIcon>
                <img width="32" src={`/images/navIcons_disabled/${pages.icon}`} alt="" />
              </ListItemIcon>
              <ListItemText primary={pages.name} />
            </ListItem>
          ))}
        </List>
      ) : (
        <List>
          {isLogin ? (
            <SidebarListContent setBurgerOpen={setBurgerOpen} />
          ) : (
            <NavbarListContent setBurgerOpen={setBurgerOpen} />
          )}
          {isTablet && <LanguageSelector theme="light" />}
        </List>
      )}
    </Wrapper>
  );
};

const NavbarListContent = ({ setBurgerOpen }: SidebarProps) => {
  // @ts-ignore
  const { t } = useTranslation();

  const currentLang = useCurrentLang();
  const navbarLinks = t('pages.navbar.links', { returnObjects: true });
  const navigate = useNavigate();
  const auth = useAppSelector(getAuthSelector);

  const redirHandler = (url: string) => {
    setBurgerOpen(false);
    // 若url開頭為'/auth', 則不修改url
    if (url.startsWith('/auth')) {
      navigate(url);
      return;
    }
    navigate(`/${currentLang}${url}`);
  };

  return (
    <>
      {navbarLinks.map((pages) => (
        <ListItem button key={pages.name} onClick={() => redirHandler(pages.url)}>
          <ListItemText primary={pages.name} />
        </ListItem>
      ))}

      <ListItem button onClick={() => redirHandler(auth.isLogin ? '/home' : '/auth/signin')}>
        <ListItemText primary={t('buttons.signIn')} />
      </ListItem>
      <ListItem button onClick={() => redirHandler(auth.isLogin ? '/home' : '/auth/signup')}>
        <ListItemText primary={t('buttons.register')} />
      </ListItem>
    </>
  );
};

const SidebarListContent = ({ setBurgerOpen }: SidebarProps) => {
  // @ts-ignore
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
  const auth = useAppSelector(getAuthSelector);
  const notifications = useAppSelector(getNotificationSelector);
  const { userData } = auth;
  const { notify } = notifications;
  const isReviewer = hasPblRole(userData, 'pbl_vetting_reviewer');
  const isOcUser = isPblOcAdminOrStaff(userData);
  const isPblAdmin = hasPblRole(userData, 'pbl_admin');
  const isLawyer = isPblLawyer(userData);
  const isCitizen = isPblCitizen(userData);
  const assistedCitizen = Boolean(getPblAssistanceToken());
  const hasPblSidebarRole = isReviewer || isOcUser || isPblAdmin || isLawyer || isCitizen;
  const isPblApplication = isPblApplicationContext({
    activeApplication: auth.activeApplication,
    pathname,
  });
  const isPblSidebar = isPblApplication && (hasPblSidebarRole || isPblApplicationPath(pathname));
  const isPblNavigationLocked = isLawyer && getPblLawyerVettingStatus(userData) === 'resubmit_required';
  const [pblUnreadCount, setPblUnreadCount] = useState(0);
  const pblManageCaseUrl = isCitizen ? PATH.pbl.citizenCases : isLawyer ? PATH.pbl.lawyerCases : PATH.pbl.cases;
  const pblCaseLink = isReviewer
    ? {
        name: t('pbl.sidebar.attorneyReview'),
        icon: 'Deals.svg',
        url: PATH.pbl.vettingApplications,
        children: [],
      }
    : {
        name: t('pbl.sidebar.manageCase'),
        icon: 'Deals.svg',
        url: pblManageCaseUrl,
        children: [],
      };
  const pblOfficeLinks = [
    { name: t('pbl.sidebar.dashboard'), icon: 'Dashboard.svg', url: PATH.pbl.dashboard, children: [] },
    { name: t('pbl.sidebar.notifications'), icon: 'Notifications.svg', url: PATH.pbl.notifications, children: [] },
    pblCaseLink,
    ...(isCitizen
      ? [{ name: t('pbl.sidebar.caseApplication'), icon: 'Deals.svg', url: PATH.pbl.citizenCaseCreate, children: [] }]
      : []),
    ...(isLawyer
      ? [{ name: t('pbl.lawyerProfile.title'), icon: 'Deals.svg', url: PATH.pbl.lawyerCv, children: [] }]
      : []),
    ...(isOcUser || isPblAdmin
      ? [
          {
            name: t('pbl.sidebar.createCitizen'),
            icon: 'Deals.svg',
            url: PATH.pbl.ocCitizenRegistration,
            children: [],
          },
          { name: t('pbl.sidebar.citizenList'), icon: 'Deals.svg', url: PATH.pbl.ocCitizens, children: [] },
        ]
      : []),
    ...(isOcUser && !assistedCitizen
      ? [{ name: t('pbl.lawyerDirectory.title'), icon: 'Deals.svg', url: PATH.pbl.ocLawyerDirectory, children: [] }]
      : []),
    { name: t('pbl.sidebar.eventCalendar'), icon: 'Event Calendar.svg', url: PATH.pbl.calendar, children: [] },
  ];
  const sidebarLinks = isPblSidebar
    ? pblOfficeLinks
    : resolveBlockchainNavigationLinks(resolveAiMediaNavigationLinks(t('pages.sidebarLinks', { returnObjects: true }) as any[]));

  useEffect(() => {
    if (!isPblSidebar) return;

    let isMounted = true;

    getPblNotifications({ status: 'sent', per_page: 1, page: 1 })
      .then((response) => {
        if (!isMounted) return;
        if (response.data.success === false) return;
        setPblUnreadCount(response.data.data.total || 0);
      })
      .catch(() => {
        if (isMounted) setPblUnreadCount(0);
      });

    return () => {
      isMounted = false;
    };
  }, [isPblSidebar]);

  // 計算未讀通知數量
  const notifyCount = () => {
    if (!notify) return 0;
    const unread = notify.filter((n: any) => n.type !== '1' && n.status === 1);
    return unread.length;
  };

  const toggleOpen = (key: string) => {
    setOpenMap((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const redirHandler = (url: string) => {
    setBurgerOpen(false);
    navigate(url);
  };

  return (
    <>
      {sidebarLinks.map((pages) => {
        const hasChildren = Array.isArray(pages.children) && pages.children.length > 0;
        const key = pages.icon || pages.name;

        if (!hasChildren) {
          return (
            <ListItem
              button
              disabled={isPblNavigationLocked}
              key={pages.name}
              onClick={isPblNavigationLocked ? undefined : () => redirHandler(pages.url)}
            >
              <ListItemIcon>
                <img width="32" src={`/images/navIcons/${pages.icon}`} alt="" />
              </ListItemIcon>
              <ListItemText primary={pages.name} />
              {pages.url === PATH.home.notifications && notifyCount() !== 0 && (
                <UnreadCounter>{notifyCount()}</UnreadCounter>
              )}
              {pages.url === PATH.pbl.notifications && pblUnreadCount !== 0 && (
                <UnreadCounter>{pblUnreadCount}</UnreadCounter>
              )}
              {pages.url === '/chat' && userData?.unread_message_count > 0 && (
                <UnreadCounter>{userData.unread_message_count}</UnreadCounter>
              )}
            </ListItem>
          );
        }

        return (
          <div key={pages.name}>
            <ListItem
              button
              disabled={isPblNavigationLocked}
              onClick={isPblNavigationLocked ? undefined : () => toggleOpen(key)}
            >
              <ListItemIcon>
                <img width="32" src={`/images/navIcons/${pages.icon}`} alt="" />
              </ListItemIcon>
              <ListItemText primary={pages.name} />
              <img
                style={openMap[key] ? { transform: 'rotateZ(90deg)', transition: '.5s' } : { transition: '.5s' }}
                width="32"
                src={`/images/navIcons/ArrowRight.svg`}
                alt=""
              />
            </ListItem>
            <Collapse in={!!openMap[key]} timeout="auto" unmountOnExit>
              <CollapsWrapper>
                <List component="div" disablePadding>
                  {pages.children.map((childrenPage: any) => (
                    <ListItem
                      button
                      disabled={isPblNavigationLocked}
                      key={childrenPage.name}
                      onClick={isPblNavigationLocked ? undefined : () => redirHandler(childrenPage.url)}
                    >
                      <ListItemIcon>
                        <img width="32" src={`/images/navIcons/${childrenPage.icon}`} alt="" />
                      </ListItemIcon>
                      <ListItemText primary={childrenPage.name} />
                    </ListItem>
                  ))}
                </List>
              </CollapsWrapper>
            </Collapse>
          </div>
        );
      })}
    </>
  );
};

export default Sidebar;
